import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardBannerModal from './DashboardBannerModal';
import { openBannerLink } from './bannerLinkUtils';

const BANNER_INTERVAL_MS = 5000;
const SLIDE_TRANSITION_MS = 500;

const getPackBannerSrc = (banner, isMobile, isDarkTheme) => {
  const hasDarkBannerAssets = Boolean(banner?.bannerDark && banner?.mobileBannerDark);
  const useDarkPackBanner = isDarkTheme && hasDarkBannerAssets;

  if (!banner) return null;

  return isMobile
    ? useDarkPackBanner
      ? banner.mobileBannerDark
      : banner.mobileBanner
    : useDarkPackBanner
      ? banner.bannerDark
      : banner.banner;
};

const DashboardBannerCarousel = ({
  banners = [],
  isMobile,
  isDarkTheme,
  className = 'p-3 md:p-0 mb-5',
  showControls = false,
}) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [activeModalDetails, setActiveModalDetails] = useState(null);
  const resetTimeoutRef = useRef(null);

  const bannerList = useMemo(() => {
    if (!banners) return [];
    if (Array.isArray(banners)) return banners;
    return [banners];
  }, [banners]);

  const visibleBanners = useMemo(() => {
    const sorted = [...bannerList].sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0));
    return sorted
      .map((banner) => ({
        banner,
        src: getPackBannerSrc(banner, isMobile, isDarkTheme),
      }))
      .filter(({ src }) => Boolean(src));
  }, [bannerList, isMobile, isDarkTheme]);

  const slideCount = visibleBanners.length;
  const isCarousel = slideCount > 1;

  const carouselSlides = useMemo(() => {
    if (!isCarousel) return visibleBanners;
    return [...visibleBanners, visibleBanners[0]];
  }, [visibleBanners, isCarousel]);

  const slideOffsetPercent = isCarousel ? (activeIndex * 100) / carouselSlides.length : 0;

  useEffect(() => {
    setActiveIndex(0);
    setIsTransitionEnabled(true);
    setShowBannerModal(false);
    setActiveModalDetails(null);
  }, [bannerList, isMobile, isDarkTheme]);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isCarousel) return undefined;

    const timeout = setTimeout(() => {
      setActiveIndex((prev) => prev + 1);
    }, BANNER_INTERVAL_MS);

    return () => clearTimeout(timeout);
  }, [isCarousel, slideCount, activeIndex]);

  const handleTransitionEnd = useCallback(() => {
    if (!isCarousel || activeIndex !== slideCount) return;

    setIsTransitionEnabled(false);
    setActiveIndex(0);

    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = setTimeout(() => {
      setIsTransitionEnabled(true);
    }, 20);
  }, [activeIndex, isCarousel, slideCount]);

  const goNext = () => setActiveIndex((prev) => (prev >= slideCount ? prev : prev + 1));

  const goPrev = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
      return;
    }
    // from the first slide, jump to the cloned last slide with no transition, then slide back one
    setIsTransitionEnabled(false);
    setActiveIndex(slideCount);
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = setTimeout(() => {
      setIsTransitionEnabled(true);
      setActiveIndex(slideCount - 1);
    }, 20);
  };

  const handleBannerClick = (banner) => {
    if (banner?.url) {
      openBannerLink(banner.url, navigate);
      return;
    }
    if (banner?.modalDetails) {
      setActiveModalDetails(banner.modalDetails);
      setShowBannerModal(true);
    }
  };

  if (slideCount === 0) return null;

  if (!isCarousel) {
    const { banner, src } = visibleBanners[0];
    const isClickable = Boolean(banner?.url || banner?.modalDetails);

    return (
      <>
        <img
          src={src}
          className={`${className} w-full ${isClickable ? 'cursor-pointer' : ''}`}
          alt={banner?.title || ''}
          onClick={() => handleBannerClick(banner)}
        />
        <DashboardBannerModal
          open={showBannerModal}
          onOpenChange={setShowBannerModal}
          modalDetails={activeModalDetails}
        />
      </>
    );
  }

  return (
    <>
      <div className={`w-full ${className}`}>
        <div className="relative overflow-hidden">
          <div
            className="flex w-full"
            style={{
              transform: `translateX(-${activeIndex * 100}%)`,
              transition: isTransitionEnabled ? `transform ${SLIDE_TRANSITION_MS}ms ease-in-out` : 'none',
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {carouselSlides.map(({ banner, src }, index) => {
              const isClickable = Boolean(banner?.url || banner?.modalDetails);
              return (
                <div
                  key={`${banner?.title ?? 'banner'}-${index}`}
                  className="min-w-full flex-[0_0_100%] shrink-0 grow-0"
                >
                  <img
                    src={src}
                    className={`block w-full h-auto ${isClickable ? 'cursor-pointer' : ''}`}
                    alt={banner?.title || ''}
                    onClick={() => handleBannerClick(banner)}
                  />
                </div>
              );
            })}
          </div>
          {showControls &&
            [
              { label: 'Previous banner', onClick: goPrev, side: 'left-4 md:left-2', Icon: CaretLeftIcon },
              { label: 'Next banner', onClick: goNext, side: 'right-4 md:right-2', Icon: CaretRightIcon },
            ].map(({ label, onClick, side, Icon }) => (
              <button
                key={label}
                type="button"
                aria-label={label}
                onClick={onClick}
                className={`absolute ${side} top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 text-white opacity-80 hover:opacity-100 transition-opacity duration-200 cursor-pointer drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]`}
              >
                <Icon size={22} weight="bold" />
              </button>
            ))}
        </div>

        {showControls && (
          <div className="flex justify-center items-center gap-1.5 mt-2">
            {visibleBanners.map(({ banner }, i) => (
              <button
                key={`${banner?.title ?? 'banner'}-dot-${i}`}
                type="button"
                aria-label={`Go to banner ${i + 1}`}
                aria-current={activeIndex % slideCount === i}
                onClick={() => setActiveIndex(i)}
                className={`h-1.5 rounded-full bg-[var(--q3-neutral-default)] transition-all duration-300 cursor-pointer ${activeIndex % slideCount === i ? 'w-4 opacity-40' : 'w-1.5 opacity-30'}`}
              />
            ))}
          </div>
        )}
      </div>

      <DashboardBannerModal
        open={showBannerModal}
        onOpenChange={setShowBannerModal}
        modalDetails={activeModalDetails}
      />
    </>
  );
};

export default DashboardBannerCarousel;
